import { type RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'
import { preloadLayouts } from '~/app/layouts'
import { adminRoutes } from '~/modules/admin/routes'
import { affiliateRoutes } from '~/modules/affiliate/routes'
import { authRoutes, authSettingsRoutes } from '~/modules/auth/routes'
import { commissionRoutes } from '~/modules/commission/routes'
import { insightsRoutes } from '~/modules/insights/routes'
import { institutionRoutes } from '~/modules/institution/routes'
import { landlordRoutes } from '~/modules/landlord/routes'
import { marketplaceRoutes } from '~/modules/marketplace/routes'
import { onboardingRoutes } from '~/modules/onboarding/routes'
import type { FlagKey } from '~/modules/rollout/constants'
import { supportRoutes } from '~/modules/support/routes'
import { tenancyRoutes } from '~/modules/tenancy/routes'
import { registerGuards } from './guards'
import { scrollBehavior } from './scroll'
import { workspaceRoutes } from './workspaces'

/**
 * Route metadata the guards read, declared so a typo in `meta` is a compile error rather than a
 * check that silently never runs.
 *
 * It lives here rather than in `env.d.ts` because a `declare module` in a file with no
 * top-level import or export *replaces* the module instead of augmenting it — which quietly
 * removes every one of vue-router's real exports.
 */
declare module 'vue-router' {
  interface RouteMeta {
    // Deliberately `string` rather than a union of the four layout names: the module route
    // tables build it from shared constants, and narrowing it here would mean touching every
    // one of them for no benefit this phase needs.
    layout?: string
    public?: boolean
    requiresAuth?: boolean
    devOnly?: boolean
    workspace?: string
    capability?: string
    title?: string
    layoutProps?: Record<string, unknown>
    /**
     * The feature flag this route is behind (phase-14 §2.1). A flagged-off route resolves to
     * the not-found page — **absent, not disabled** — and the menu entry that leads to it is
     * filtered out by the same flag.
     */
    flag?: FlagKey
  }
}

/**
 * Routes are declared per module and merged here. Every route carries `meta.layout`; dashboard
 * routes additionally carry `meta.workspace` / `meta.capability`, which the guards enforce.
 *
 * The full map is docs/v2/route-map.md — a route exists there before it is built.
 */
const routes: RouteRecordRaw[] = [
  // Phase 08. The public marketplace owns `/`, `/listings`, `/properties` and
  // `/list-your-property` (`/locations` redirects into `/listings` since 2026-10-28). Declared first so the front door is the first thing matched.
  ...marketplaceRoutes,

  ...authRoutes,

  // `/app` has no page of its own: the workspace guard turns it into the user's default
  // workspace, which is the one place that decision is made. Deliberately NOT a static
  // `redirect` — vue-router resolves those before guards run, so a multi-role user would be
  // sent to a hard-coded workspace before anyone asked the server which one is theirs.
  {
    path: '/app',
    name: 'app',
    component: () => import('~/modules/platform/pages/RedirectingPage.vue'),
    meta: { layout: 'blank', requiresAuth: true },
  },
  ...workspaceRoutes,
  // A workspace that owns its route tree registers it here. `workspaceRoutes` skips those, so
  // there is no placeholder shadowing `/app/affiliate` or `/app/landlord`.
  ...affiliateRoutes,
  ...landlordRoutes,
  // Phase 12. The administration workspace now owns its tree, home page included.
  ...adminRoutes,
  // 2026-10-12. The Support workspace (requesters-support-whatsapp.md §6).
  ...supportRoutes,
  // 2026-10-22. An affiliate institution's workspace.
  ...institutionRoutes,
  // Phase 05. Declared after `landlordRoutes` so the property detail route is matched first;
  // the onboarding paths are longer and cannot be shadowed by it.
  ...onboardingRoutes,
  // Phase 07. Tenancies, renewals and payments for both workspaces, declared once.
  ...tenancyRoutes,
  // Phase 10. The officer's commission screens and the administrator's console.
  ...commissionRoutes,
  // Phase 11. Analytics and the notification centre, for all three workspaces.
  ...insightsRoutes,
  ...authSettingsRoutes,

  {
    // Phase 13. The global error page: the correlation id travels as `?ref=`.
    path: '/500',
    name: 'server-error',
    component: () => import('~/modules/platform/pages/ErrorPage.vue'),
    meta: { layout: 'blank', public: true },
  },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('~/modules/platform/pages/ForbiddenPage.vue'),
    meta: { layout: 'blank', public: true },
  },
  {
    path: '/dev/kitchen-sink',
    name: 'kitchen-sink',
    component: () => import('~/modules/platform/pages/KitchenSinkPage.vue'),
    meta: {
      layout: 'dashboard',
      public: true,
      devOnly: true,
      layoutProps: { brand: 'APMP', title: 'Kitchen sink' },
    },
  },
  {
    path: '/dev/auth-preview',
    name: 'auth-preview',
    component: () => import('~/modules/platform/pages/AuthPreviewPage.vue'),
    meta: { layout: 'auth', public: true, devOnly: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('~/modules/platform/pages/NotFoundPage.vue'),
    meta: { layout: 'blank', public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  // A fresh load starts at the top; back and forward wait for their page (`./scroll`).
  scrollBehavior,
})

// Development-only routes must never ship. The mocking build is not shipped either -- it is what
// the E2E suite runs against (playwright.config.ts) -- so it keeps them.
router.beforeEach((to) => {
  if (to.meta.devOnly && !import.meta.env.DEV && import.meta.env.MODE !== 'mocking') {
    return { name: 'not-found' }
  }
  return true
})

registerGuards(router)

// The destination's layout is a lazy chunk, like its page, and is loaded the same way: before
// the navigation commits, so a page never renders without its chrome (`~/app/layouts`).
preloadLayouts(router)
